import { j as imageResolutionLevels } from './pp-product-config-DylFCRxi.mjs';
import './prodsel.store-B-MQAyQE.mjs';

const resolutionOrder = ['notRecommended', 'low', 'good', 'perfect'];

function getWorstResolutionLevel(media) {
    const levels = (media || []).flatMap((item) => item?._images || [])
        .map((image) => image.resolutionLevel)
        .filter((level) => resolutionOrder.includes(level));
    return levels.sort((first, second) => resolutionOrder.indexOf(first) - resolutionOrder.indexOf(second))[0] || null
}

function getResolutionWarnings(media) {
    const images = (media || []).flatMap((item) => item?._images || []);
    return ['notRecommended', 'low'].map((level) => {
        const affected = images.filter((image) => image.resolutionLevel === level);
        const pages = affected.map((image) => Number(image.onPage));
        return {
            level,
            count: affected.length,
            pages: pages.every((page) => Number.isInteger(page) && page > 0)
                ? [...new Set(pages)].sort((first, second) => first - second) : []
        }
    }).filter((warning) => warning.count)
}

function requiresResolutionConfirmation(level, fromLevel) {
    return resolutionOrder.includes(level) && resolutionOrder.includes(fromLevel)
        && resolutionOrder.indexOf(level) <= resolutionOrder.indexOf(fromLevel)
}

function parseInfo(media) {
    const component = Array.isArray(media?.components)
        ? media.components.find((item) => item?.info && (item.type === 'pdf' || item.type === 'skript' || item.module === media.module))
        : null;
    const rawInfo = component?.info ?? media?.info;

    if (typeof rawInfo === 'object' && rawInfo) return rawInfo
    if (typeof rawInfo !== 'string' || !rawInfo.trim()) return null

    try {
        return JSON.parse(rawInfo)
    } catch {
        return null
    }
}

function getTargetSize(info) {
    const width = Number(info?.pageinfo?.trimWidth ?? info?.pageinfo?.size?.trimwidth);
    const height = Number(info?.pageinfo?.trimHeight ?? info?.pageinfo?.size?.trimheight);
    return width > 0 && height > 0 ? { width, height } : null
}

const formatAdjustmentTolerance = 0.03;

function getFitinResource(product, maxSize) {
    const width = Number(product?.widthMM);
    const height = Number(product?.heightMM);
    const limit = Number(maxSize);
    if (!(width > 0) || !(height > 0) || !(limit > 0)) return null

    const scale = limit / Math.max(width, height);
    return `${Math.round(width * scale)}x${Math.round(height * scale)}`
}

function needsFormatAdjustment(media, product) {
    if (media?.module !== 'calendar') return false
    const sourceSize = getTargetSize(parseInfo(media));
    const targetWidth = Number(product?.widthMM);
    const targetHeight = Number(product?.heightMM);
    if (!sourceSize || !(targetWidth > 0) || !(targetHeight > 0)) return false

    const deviation = (sourceSize.width / sourceSize.height) / (targetWidth / targetHeight);
    return Math.abs(deviation - 1) > formatAdjustmentTolerance
}

function getResolutionLevel(dpi) {
    if (dpi < imageResolutionLevels.notRecommended) return 'notRecommended'
    if (dpi < imageResolutionLevels.low) return 'low'
    if (dpi < imageResolutionLevels.good) return 'good'
    return 'perfect'
}

function calculateImageResolutions(media, product) {
    const info = parseInfo(media);
    const sourceSize = getTargetSize(info);
    const targetWidth = Number(product?.widthMM);
    const targetHeight = Number(product?.heightMM);
    const images = Array.isArray(info?.image) ? info.image : [];

    if (!sourceSize || targetWidth <= 0 || targetHeight <= 0) return []

    const scale = Math.max(targetWidth / sourceSize.width, targetHeight / sourceSize.height);

    return images.map((image) => {
        const dpiX = Number(image?.DpiX);
        const dpiY = Number(image?.DpiY);
        const targetDpiX = Number.isFinite(dpiX) ? dpiX / scale : null;
        const targetDpiY = Number.isFinite(dpiY) ? dpiY / scale : null;
        const dpi = Math.min(targetDpiX ?? Infinity, targetDpiY ?? Infinity);

        return {
            ...image,
            DpiX: targetDpiX,
            DpiY: targetDpiY,
            dpi,
            resolutionLevel: Number.isFinite(dpi) ? getResolutionLevel(dpi) : null,
        }
    })
}

function updateMediaImageResolutions(media, product) {
    if (!Array.isArray(media)) return media

    media.forEach((item) => {
        item._images = calculateImageResolutions(item, product);
    });

    return media
}

export { getResolutionWarnings as a, getFitinResource as b, calculateImageResolutions as c, getWorstResolutionLevel as g, needsFormatAdjustment as n, requiresResolutionConfirmation as r, updateMediaImageResolutions as u };
